import React, { useEffect, useMemo, useState } from 'react'

import { Field, Form, Formik } from 'formik'
import _find from 'lodash/find'
import { useRouter } from 'next/router'
import { toast } from 'react-toastify'

import { setDealAdvisorOpen, setDealAdvisorTab, setDocDrawModalOpen } from '@/client/generalSlice'
import { useAppDispatch } from '@/client/hooks'
import LoadingIcon from '@/components/LoadingIcon'
import Stepper from '@/components/Stepper'
import { KeyCurrencyField } from '@/key-components/formik/KeyCurrencyField'
import { KeyInputField } from '@/key-components/formik/KeyInputField'
import { KeySelectField } from '@/key-components/formik/KeySelectField'
import { useDocDrawMutation } from '@/services/documents'
import { useGetAllStatesQuery } from '@/services/states'
import { useListApplicationPartiesQuery } from '@/services/v2-application/applicationParties'
import { useListApplicationProfilesQuery } from '@/services/v2-application/applicationProfile'
import { useGetExpensesByApplicationQuery } from '@/services/v2-application/expense'
import { useGetValuationsByApplicationQuery } from '@/services/v2-application/valuation'
import { toastDefaultOptions } from '@/utils/toast'

const DocDraw = ({ application }) => {
  const dispatch = useAppDispatch()
  const [docDraw] = useDocDrawMutation()
  const applicationExternalId = application.externalId
  const router = useRouter()
  const { id } = router.query
  const [currentStep, setCurrentStep] = useState(0)
  const [firstLoad, setFirstLoad] = useState(true)

  const [initialValues, setInitialValues] = useState<any>({
    property_place_incorporation: '',
    action: '',
    settlement_agent: '',
    effective_date: '',
    trustee: '',
    owner_vesting_type: '',
    same_mailing_address: false,
    different_mailing_address: false,
    street: '',
    unit_number: '',
    city: '',
    state: '',
    zip_code: '',
    app_street: '',
    app_unit_number: '',
    app_city: '',
    app_state: '',
    app_zip_code: '',
    county: '',
    legal_description: '',
    stamp_tax: '',
    intangible_tax: '',
    recordation_tax: '',
    spousal_consent: false,
    spouse_full_name: '',
    spouse_required_on_security_inst: '',
    spouse_required_exceptions: '',
    owner_trust_name: '',
    owner_entity_name: '',
  })

  const { data: applicationParties } = useListApplicationPartiesQuery(application?.uuid, {
    skip: !application?.uuid,
  })
  const { data: applicationProfiles } = useListApplicationProfilesQuery(application?.uuid, {
    skip: !application?.uuid,
  })
  const { data: expenses } = useGetExpensesByApplicationQuery(
    { applicationUUID: application?.uuid },
    {
      skip: !application,
    }
  )
  const { data: valuations } = useGetValuationsByApplicationQuery(application?.uuid, {
    skip: !application?.uuid,
  })

  const { data: allStates } = useGetAllStatesQuery({})
  const { data: nonMortgageStates } = useGetAllStatesQuery({ trustee: 'Not Required' })

  const nonMortgageStatesAbbreviations = useMemo(
    () => nonMortgageStates?.results?.map((state) => state.state_abbreviation),
    [nonMortgageStates]
  )

  const allTrusteeOptions = useMemo(() => {
    if (!allStates?.results) return []
    const trustees = allStates.results.map((state) => state.trustee)
    const uniqueTrustees = trustees.filter(
      (trustee, index) => trustee !== null && trustees.indexOf(trustee) === index
    )
    return uniqueTrustees
      .map((trustee) => ({
        value: trustee || '',
        label: trustee || '',
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [allStates])

  const stateTrustee = allStates?.results?.find(
    (state) => state.state_abbreviation === application?.property?.address?.state
  )

  const applicant = _find(applicationProfiles?.results, {
    position: 1,
  })

  const isSameAddress = () => {
    return (
      applicant?.profile?.mailing_address?.street === application?.property?.address?.street &&
      applicant?.profile?.mailing_address?.city === application?.property?.address?.city &&
      applicant?.profile?.mailing_address?.state === application?.property?.address?.state &&
      applicant?.profile?.mailing_address?.zip_code === application?.property?.address?.zipCode &&
      applicant?.profile?.mailing_address?.unit_number ===
        application?.property?.address?.unitNumber
    )
  }

  useEffect(() => {
    try {
      setFirstLoad(true)
      if (application && applicationParties && applicationProfiles && expenses && valuations) {
        const trustee = applicationParties?.results?.find(
          (ap) => ap.application_party_type === 'trustee'
        )

        const titleReport = application?.titleReport
        const property = application?.property
        const taxStamp = expenses?.results?.find(
          (expense) => expense?.name === 'State Mortgage Tax/Stamps'
        )
        const intangibleTax = expenses?.results?.find(
          (expense) => expense?.name === 'Intangible Tax Fee'
        )
        const countyTax = expenses?.results?.find(
          (expense) => expense?.name === 'County Mortgage Tax/Stamps'
        )
        const recordationTax =
          property?.address?.state === 'VA'
            ? countyTax && taxStamp
              ? `${Number(countyTax?.amount) + Number(taxStamp?.amount)}`
              : ''
            : ''

        const propertyPlaceIncorporation =
          property?.address?.state === 'MI'
            ? titleReport?.legalDescription?.toLowerCase()?.includes('city')
              ? 'city'
              : titleReport?.legalDescription?.toLowerCase()?.includes('township')
                ? 'township'
                : ''
            : ''

        setInitialValues({
          property_place_incorporation: propertyPlaceIncorporation,
          settlement_agent: titleReport?.settlementAgent || '',
          effective_date: application?.agreementEffectiveOn || '',
          trustee: nonMortgageStatesAbbreviations?.includes(property.address.state)
            ? 'Not Required'
            : trustee?.party?.name || '',
          owner_vesting_type: property?.ownerVestingType || '',
          street: applicant?.profile?.mailing_address?.street || '',
          unit_number: applicant?.profile?.mailing_address?.unit_number || '',
          city: applicant?.profile?.mailing_address?.city || '',
          state: applicant?.profile?.mailing_address?.state || '',
          zip_code: applicant?.profile?.mailing_address?.zip_code || '',
          app_street: property.address?.street || '',
          app_unit_number: property.address?.unitNumber || '',
          app_city: property.address?.city || '',
          app_state: property.address?.state || '',
          app_zip_code: property.address?.zipCode || '',
          county: property.address?.county || '',
          same_mailing_address: isSameAddress(),
          different_mailing_address: !isSameAddress(),
          legal_description: titleReport?.legalDescription || '',
          stamp_tax: property.address?.state === 'FL' ? taxStamp?.amount || '' : '',
          intangible_tax: property.address?.state === 'FL' ? intangibleTax?.amount || '' : '',
          recordation_tax: recordationTax || '',
          spousal_consent: !!titleReport?.nooNameOfSpouse,
          spouse_full_name: titleReport?.nooNameOfSpouse || '',
          spouse_required_on_security_inst: '',
          spouse_required_exceptions: '',
          owner_trust_name:
            property?.ownerVestingType === 'trust' ? property?.titleVesting || '' : '',
          owner_entity_name:
            property?.ownerVestingType === 'entity' ? property?.titleVesting || '' : '',
        })
        setFirstLoad(false)
      }
    } catch (error) {
      console.error(error)
    }
  }, [
    application,
    applicationParties,
    applicationProfiles,
    expenses,
    valuations,
    nonMortgageStatesAbbreviations,
    setFirstLoad,
  ])

  const handleClickLabel = (page: string, target: string) => () => {
    if (target === 'trustee-deal') {
      dispatch(setDealAdvisorOpen(true))
      dispatch(setDealAdvisorTab('fileContacts'))
      dispatch(setDocDrawModalOpen(false))
      return
    }
    const splittedPathname = router.pathname.split('/')
    if (
      (splittedPathname[splittedPathname.length - 1] === id && page === '') ||
      router.pathname.includes(target)
    ) {
      router.push(`#${target}`)
      dispatch(setDocDrawModalOpen(false))
      return
    }
    router.push(`/applications/${id}/${page}#${target}`)
    dispatch(setDocDrawModalOpen(false))
  }

  const toastSettings = {
    ...toastDefaultOptions,
    isLoading: false,
    autoClose: 5000,
    closeOnClick: true,
    closeButton: true,
  }

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true)

    const toastId = toast.loading('Drawing Docs...', toastSettings)

    return docDraw({
      property_place_incorporation: values.property_place_incorporation,
      application_external_id: applicationExternalId,
    })
      .unwrap()
      .then(() => {
        toast.update(toastId, {
          render: (
            <div>
              <div className='text-base'>Doc draw request successful</div>
              <div>
                <p className='text-sm'>The docs are currently being processed.</p>
                <p className='text-xs'>
                  You can see updates about the processing on the streaming notes.
                </p>
              </div>
            </div>
          ),
          type: 'success',
          ...toastSettings,
        })
        dispatch(setDocDrawModalOpen(false))
      })
      .catch((error) => {
        if (Array.isArray(error.data))
          toast.update(toastId, {
            render: <ul>{error.data?.map((error) => <li key={error}>{error}</li>)}</ul>,
            type: 'error',
            ...toastSettings,
          })
        else
          toast.update(toastId, {
            render: 'An error occured when trying to generate the document. Please try again',
            type: 'error',
            ...toastSettings,
          })
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  const getNextButtonDisabled = (step, values) => {
    if (step === 0) {
      if (!nonMortgageStatesAbbreviations?.includes(values.app_state))
        return !(values.effective_date && values.trustee)
      return !values.effective_date
    }
    if (step === 1) {
      return !values.owner_vesting_type
    }
    if (step === 2) {
      if (values.same_mailing_address && !values.different_mailing_address)
        return !(
          values.app_street &&
          values.app_city &&
          values.app_state &&
          values.app_zip_code &&
          values.legal_description &&
          values.county &&
          (values.app_state === 'MI' ? values.property_place_incorporation : true)
        )
      if (values.different_mailing_address)
        return !(
          values.street &&
          values.city &&
          values.state &&
          values.zip_code &&
          values.legal_description &&
          values.county &&
          (values.same_mailing_address || values.different_mailing_address) &&
          (values.app_state === 'MI' ? values.property_place_incorporation : true)
        )
      return true
    }
    if (step === 3) {
      if (values.app_state === 'FL') {
        return !(values.stamp_tax !== '' && values.intangible_tax !== '')
      }
      if (values.app_state === 'VA') {
        return values.recordation_tax === ''
      }
      return false
    }
    return false
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, setFieldValue, values, submitForm }) => (
        <Form>
          <Stepper
            steps={[
              {
                title: 'Closing Information',
                content: (step) => {
                  setCurrentStep(step)
                  return (
                    <div className='h-full'>
                      <div>
                        {firstLoad ? (
                          <div className='w-full flex justify-center items-center p-4'>
                            <LoadingIcon
                              height={40}
                              width={40}
                            />
                          </div>
                        ) : (
                          <div className='w-full text-[#3f3f3f] font-light'>
                            <div className='w-full mb-4'>
                              <h5
                                className='mb-1 text-[14px] text-gray-500 cursor-pointer'
                                onClick={handleClickLabel('underwriting', 'title-report')}
                              >
                                Settlement Agent #
                              </h5>
                              <KeyInputField
                                placeholder=''
                                name='settlement_agent'
                                showError
                                className='w-full'
                                disabled
                              />
                            </div>
                            <div className='w-full mb-4'>
                              <h5
                                className='mb-1 text-[14px] text-gray-500 cursor-pointer'
                                onClick={handleClickLabel('underwriting', 'ics-closing-info')}
                              >
                                Effective Date *
                              </h5>
                              <KeyInputField
                                placeholder=''
                                name='effective_date'
                                showError
                                className='w-full'
                                type='date'
                                disabled
                              />
                              <h6 className='ml-3 mb-2 text-[12px] text-gray-500'>
                                Date that contract will be effective - that is, the date of
                                scheduled signing.
                              </h6>
                            </div>
                            <div className='w-full'>
                              <h5
                                className='mb-1 text-[14px] text-gray-500 cursor-pointer'
                                onClick={handleClickLabel('', 'trustee-deal')}
                              >
                                Trustee *
                              </h5>
                              <Field
                                name='trustee'
                                placeholder=''
                                component={KeySelectField}
                                options={allTrusteeOptions}
                                className='w-full'
                                disabled
                              />
                              {(stateTrustee?.trustee !== values.trustee ||
                                stateTrustee?.trustee === '' ||
                                stateTrustee?.trustee === null) && (
                                <h6 className='ml-3 text-[12px] text-red-500'>
                                  The selected Trustee is not valid for this state. Please change it
                                  to the correct one.
                                </h6>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                },
                buttonLabel: 'Next',
                disabledButton: getNextButtonDisabled(currentStep, values),
              },
              {
                title: 'Ownership Information',
                content: (step) => {
                  setCurrentStep(step)
                  return (
                    <div className='h-full'>
                      <div className='bg-white rounded-lg'>
                        <div className='w-fulltext-[#3f3f3f] font-light'>
                          <div className='w-full mb-4'>
                            <h5
                              className='text-[14px] text-gray-500 cursor-pointer'
                              onClick={handleClickLabel('', 'property')}
                            >
                              Owner Vesting Type *
                            </h5>
                            <Field
                              name='owner_vesting_type'
                              placeholder=''
                              component={KeySelectField}
                              options={[
                                { value: 'trust', label: 'Trust' },
                                { value: 'entity', label: 'Entity' },
                                { value: 'natural', label: 'Natural Person' },
                              ]}
                              className='w-full'
                              disabled
                            />
                            <h6 className='mb-2 text-[12px] text-gray-500 gap-3 flex flex-col'>
                              <div>
                                Natural Person(s) - select if title is held by one or more
                                individual
                              </div>
                            </h6>
                          </div>
                          {values?.owner_vesting_type === 'trust' && (
                            <div className='w-full mb-4'>
                              <h5 className='mb-1 text-[14px] text-gray-500'>Owner Trust Name *</h5>
                              <KeyInputField
                                placeholder=''
                                name='owner_trust_name'
                                showError
                                className='w-full'
                                disabled
                              />
                              <h6 className='mb-2 text-[12px] text-gray-500 gap-3 flex flex-col'>
                                <div>Trust - select if title is held in a trust</div>
                              </h6>
                            </div>
                          )}
                          {values?.owner_vesting_type === 'entity' && (
                            <div className='w-full'>
                              <h5 className='mb-1 text-[14px] text-gray-500'>
                                Owner Entity Name *
                              </h5>
                              <KeyInputField
                                placeholder=''
                                name='owner_entity_name'
                                showError
                                className='w-full'
                                disabled
                              />
                              <h6 className='text-[12px] text-gray-500 gap-3 flex flex-col'>
                                <div>
                                  Entity - select if title is held by a corporation, LLC, or similar
                                </div>
                              </h6>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                },
                buttonLabel: 'Next',
                disabledButton: getNextButtonDisabled(currentStep, values),
                disabledStep: getNextButtonDisabled(0, values),
              },
              {
                title: 'Property Information',
                content: (step) => {
                  setCurrentStep(step)
                  return (
                    <div className='h-full'>
                      <div className='bg-white rounded-lg'>
                        <div>
                          <div className='mb-4 text-[#3f3f3f] font-light'>
                            <h5 className='mb-1 text-[15px]'>
                              Is the Property Address the same as the owner&apos;s Mailing Address *
                            </h5>
                            <div className='w-full mt-2 gap-6'>
                              <div className='mb-2 flex justify-between'>
                                <h5 className='w-4 text-[13px] mr-2'>Yes</h5>
                                <KeyInputField
                                  placeholder=''
                                  name='same_mailing_address'
                                  checked={values.same_mailing_address}
                                  showError
                                  useBaseClasses={false}
                                  type='radio'
                                  parentClass='flex items-start'
                                  afterChange={() => {
                                    setFieldValue('different_mailing_address', false)
                                    setFieldValue('same_mailing_address', true)
                                  }}
                                />
                                <h6 className='w-4/5 text-[13px] text-nowrap text-ellipsis overflow-hidden'>
                                  Property Address is the Mailing Address
                                </h6>
                              </div>
                              <div className='mb-2 flex justify-between'>
                                <h5 className='w-4 text-[13px] mr-2'>No</h5>
                                <KeyInputField
                                  placeholder=''
                                  checked={values.different_mailing_address}
                                  name='different_mailing_address'
                                  showError
                                  useBaseClasses={false}
                                  type='radio'
                                  parentClass='flex items-start'
                                  afterChange={() => {
                                    setFieldValue('different_mailing_address', true)
                                    setFieldValue('same_mailing_address', false)
                                  }}
                                />
                                <h6 className='w-4/5 text-[13px] text-nowrap text-ellipsis overflow-hidden'>
                                  Mailing Address is different to Property Address
                                </h6>
                              </div>
                            </div>
                            {values.same_mailing_address && (
                              <div className='grid grid-cols-3 gap-2 mt-2'>
                                <div>
                                  <div
                                    className='text-[13px] mb-2 cursor-pointer'
                                    onClick={handleClickLabel('', 'property')}
                                  >
                                    Mailing Address
                                  </div>
                                  <KeyInputField
                                    placeholder=''
                                    name='app_street'
                                    showError
                                    className='w-full'
                                    disabled
                                  />
                                </div>
                                <div>
                                  <div
                                    className='text-[13px] mb-2 cursor-pointer'
                                    onClick={handleClickLabel('', 'property')}
                                  >
                                    Unit Number
                                  </div>
                                  <KeyInputField
                                    placeholder=''
                                    name='app_unit_number'
                                    showError
                                    className='w-full'
                                    disabled
                                  />
                                </div>
                                <div>
                                  <div
                                    className='text-[13px] mb-2 cursor-pointer'
                                    onClick={handleClickLabel('', 'property')}
                                  >
                                    City
                                  </div>
                                  <KeyInputField
                                    placeholder=''
                                    name='app_city'
                                    showError
                                    className='w-full'
                                    disabled
                                  />
                                </div>
                                <div>
                                  <div
                                    className='text-[13px] mb-2 cursor-pointer'
                                    onClick={handleClickLabel('', 'property')}
                                  >
                                    State
                                  </div>
                                  <KeyInputField
                                    placeholder=''
                                    name='app_state'
                                    showError
                                    className='w-full'
                                    disabled
                                  />
                                </div>
                                <div>
                                  <div
                                    className='text-[13px] mb-2 cursor-pointer'
                                    onClick={handleClickLabel('', 'property')}
                                  >
                                    Zip Code
                                  </div>
                                  <KeyInputField
                                    placeholder=''
                                    name='app_zip_code'
                                    showError
                                    className='w-full'
                                    disabled
                                  />
                                </div>
                              </div>
                            )}
                            {values.different_mailing_address && (
                              <div className='grid grid-cols-3 gap-2 mt-2'>
                                <div>
                                  <div
                                    className='text-[13px] mb-2 cursor-pointer'
                                    onClick={handleClickLabel('', 'personal-info')}
                                  >
                                    Mailing Address
                                  </div>
                                  <KeyInputField
                                    placeholder=''
                                    name='street'
                                    showError
                                    className='w-full'
                                    disabled
                                  />
                                </div>
                                <div>
                                  <div
                                    className='text-[13px] mb-2 cursor-pointer'
                                    onClick={handleClickLabel('', 'personal-info')}
                                  >
                                    Unit Number
                                  </div>
                                  <KeyInputField
                                    placeholder=''
                                    name='unit_number'
                                    showError
                                    className='w-full'
                                    disabled
                                  />
                                </div>
                                <div>
                                  <div
                                    className='text-[13px] mb-2 cursor-pointer'
                                    onClick={handleClickLabel('', 'personal-info')}
                                  >
                                    City
                                  </div>
                                  <KeyInputField
                                    placeholder=''
                                    name='city'
                                    showError
                                    className='w-full'
                                    disabled
                                  />
                                </div>
                                <div>
                                  <div
                                    className='text-[13px] mb-2 cursor-pointer'
                                    onClick={handleClickLabel('', 'personal-info')}
                                  >
                                    State
                                  </div>
                                  <KeyInputField
                                    placeholder=''
                                    name='state'
                                    showError
                                    className='w-full'
                                    disabled
                                  />
                                </div>
                                <div>
                                  <div
                                    className='text-[13px] mb-2 cursor-pointer'
                                    onClick={handleClickLabel('', 'personal-info')}
                                  >
                                    Zip Code
                                  </div>
                                  <KeyInputField
                                    placeholder=''
                                    name='zip_code'
                                    showError
                                    className='w-full'
                                    disabled
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                          <div className='mb-4 text-[#3f3f3f] font-light'>
                            <h5 className='mb-1 text-[15px]'>Property Place Incorporation *</h5>
                            <h6 className='mb-1 mt-3 text-[13px]'>
                              Note: This is only required for MI
                            </h6>
                            <div className='w-full'>
                              <h5 className='mb-2 mt-2 text-[13px]'>City</h5>
                              <Field
                                name='property_place_incorporation'
                                placeholder=''
                                component={KeySelectField}
                                options={[
                                  { value: 'city', label: 'City' },
                                  { value: 'township', label: 'Township' },
                                ]}
                                className='w-full'
                                disabled={values.app_state !== 'MI'}
                              />
                            </div>
                          </div>
                        </div>
                        <div className='w-full'>
                          <div className='w-full'>
                            <div>
                              <h5
                                className='mb-2 mt-2 text-[13px cursor-pointer'
                                onClick={handleClickLabel('underwriting', 'title-report')}
                              >
                                Legal Description *
                              </h5>
                              <KeyInputField
                                placeholder=''
                                name='legal_description'
                                showError
                                className='w-full min-h-[100px] max-w-none'
                                isTextarea
                                disabled
                              />
                            </div>
                            <div className='text-[12px] text-gray-500 gap-3 flex flex-col'>
                              <div>
                                The entire text of the legal description INCLUDING the preamble if
                                any.
                              </div>
                              <div>
                                The preamble begins with &quot;A certain tract or parcel of land
                                lying and being in the County of {values.county} and State of{' '}
                                {values.state}&quot; or similar (e.g. &quot;Land situated in&quot;),
                                and ends with &quot;more particularly described as follows:&quot; or
                                similar.
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                },
                buttonLabel: 'Next',
                hideButton: !(
                  application?.property?.address?.state === 'FL' ||
                  application?.property?.address?.state === 'VA'
                ),
                disabledButton: getNextButtonDisabled(currentStep, values),
                disabledStep: getNextButtonDisabled(0, values) || getNextButtonDisabled(1, values),
              },
              ...(application?.property?.address?.state === 'FL' ||
              application?.property?.address?.state === 'VA'
                ? [
                    {
                      title: 'Taxes (Only Relevant for FL and VA)',
                      content: (step) => {
                        setCurrentStep(step)
                        return (
                          <div className='h-full'>
                            <div className='bg-white rounded-lg'>
                              <div className='w-fulltext-[#3f3f3f] font-light grid grid-cols-2 gap-4'>
                                <div className='w-full flex flex-col'>
                                  <h5
                                    className='mb-1 text-[14px] text-gray-500 cursor-pointer'
                                    onClick={handleClickLabel('underwriting', 'closingDetails')}
                                  >
                                    Stamp Tax (FL Only)
                                  </h5>
                                  <KeyCurrencyField
                                    placeholder=''
                                    name='stamp_tax'
                                    showError
                                    className='w-full disabled:bg-gray-200 disabled:border-gray-200 disabled:text-gray-400'
                                    fixedDecimalScale
                                    decimalScale={2}
                                    disabled
                                  />
                                </div>
                                <div className='w-full flex flex-col'>
                                  <h5
                                    className='mb-1 text-[14px] text-gray-500 cursor-pointer'
                                    onClick={handleClickLabel('underwriting', 'closingDetails')}
                                  >
                                    Intangible Tax (FL Only)
                                  </h5>
                                  <KeyCurrencyField
                                    placeholder=''
                                    name='intangible_tax'
                                    showError
                                    className='w-full disabled:bg-gray-200 disabled:border-gray-200 disabled:text-gray-400'
                                    fixedDecimalScale
                                    decimalScale={2}
                                    disabled
                                  />
                                </div>
                                <div className='w-full flex flex-col'>
                                  <h5
                                    className='mb-1 text-[14px] text-gray-500 cursor-pointer'
                                    onClick={handleClickLabel('underwriting', 'closingDetails')}
                                  >
                                    Recordation Tax (VA Only)
                                  </h5>
                                  <KeyCurrencyField
                                    placeholder=''
                                    name='recordation_tax'
                                    showError
                                    className='w-full disabled:bg-gray-200 disabled:border-gray-200 disabled:text-gray-400'
                                    fixedDecimalScale
                                    decimalScale={2}
                                    disabled
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        )
                      },
                      buttonLabel: 'Next',
                      disabledButton: getNextButtonDisabled(currentStep, values),
                      hideButton: true,
                      disabledStep:
                        getNextButtonDisabled(0, values) ||
                        getNextButtonDisabled(1, values) ||
                        getNextButtonDisabled(2, values),
                    },
                  ]
                : []),
            ]}
            finishButton={{
              label: 'Finish - Draw Docs',
              onClick: () => submitForm(),
              extraDisabled: getNextButtonDisabled(currentStep, values),
            }}
          />
        </Form>
      )}
    </Formik>
  )
}

export default DocDraw
